import { createPlaceFavList } from "@/api/places";
import Iconify from "@/components/ui/Iconify";
import { useAuthContext } from "@/context/auth/useAuthContext";
import { AnimatePresence, motion } from "framer-motion";
import { useTranslations } from "@/lib/static-intl";
import { enqueueSnackbar } from "notistack";
import { useState } from "react";

const colors = [
  "#ff596e",
  "#ff596e",
  "#ffc801",
  "#8fc049",
  "#42a37c",
  "#01c6d8",
  "#e355a9",
  "#ff98a0",
  "#8a94be",
  "#4596e2",
  "#005188",
  "#767676",
];

const FavPlaceListCreateFormSliderModal = ({
  isOpen,
  onClose,
  setPlaceFavLists,
  onListCreated,
}) => {
  const apiT = useTranslations("api");
  const placesT = useTranslations("places.place_fav_list_slider_modal");

  const { user } = useAuthContext();
  const [loader, setLoader] = useState(false);
  const [currentColor, setCurrentColor] = useState(0);

  const [createForm, setCreateForm] = useState({
    listName: "",
    color: colors[0],
    isPublic: true,
    isExposed: false,
    note: "",
    url: "",
  });

  const resetForm = () => {
    setCreateForm({
      listName: "",
      color: colors[0],
      isPublic: true,
      isExposed: true,
      note: "",
      url: "",
    });
    setCurrentColor(0);
  };

  const handleSubmit = async () => {
    try {
      if (createForm.listName.length === 0 || !user?.userId) return;
      setLoader(true);
      const payload = {
        ...createForm,
        userId: user?.userId,
      };
      const response = await createPlaceFavList(payload);
      if (response?.id) {
        console.log(response);
        setPlaceFavLists((prev) => [...prev, { ...response, type: "NEW" }]);
        // 방금 만든 목록을 바로 선택 상태로 (만들자마자 저장하는 흐름이 자연스럽다)
        onListCreated?.(response);
        onClose();
        resetForm();
      }
    } catch (error) {
      enqueueSnackbar(
        error?.response?.data?.message ||
          error?.message ||
          apiT("error message"),
        { variant: "error" }
      );
    } finally {
      setLoader(false);
    }
  };
  return (
    <AnimatePresence mode="wait">
      {isOpen && (
        <motion.div
          initial={{ y: "100%" }}
          animate={{ y: 0 }}
          exit={{ y: "100%" }}
          transition={{ duration: 0.2, ease: "easeInOut" }}
          className="absolute top-0 bottom-0 left-0 right-0 bg-white dark:bg-dark_bg_grey rounded-t-2xl flex flex-col overflow-hidden z-[92]"
        >
          <div className="w-full flex py-3 px-3 border-b dark:border-brown">
            <div className="w-full flex items-center justify-center gap-2">
              <span className="w-fit text-xl font-semibold tracking-tight text-nowrap">
                {placesT("add_new_list")}
              </span>
            </div>
            <button type="button" onClick={onClose} className="w-fit">
              <Iconify
                icon={"pepicons-pencil:angle-down"}
                className={"!min-w-6 !w-6 !h-6"}
              />
            </button>
          </div>
          <div className="w-full h-full max-h-full overflow-y-auto thin-scrollbar flex flex-col gap-4 py-3 px-4 pb-6">
            <div className="w-full flex items-center justify-between border-b dark:border-brown pb-2 px-2">
              <input
                type="text"
                placeholder={placesT("new_list_name_pl")}
                className="w-full border-none outline-none text-xl font-bold caret-red"
                value={createForm?.listName}
                onChange={(e) =>
                  setCreateForm({
                    ...createForm,
                    listName: e.target.value,
                  })
                }
                maxLength={20}
              />
              <span className="text-xs text-nowrap dark:text-white">
                {createForm?.listName?.length}
                <span className="text-black/40 dark:text-white">/20</span>
              </span>
            </div>
            <div className="w-full flex flex-col gap-3">
              <span className="font-semibold text-lg">
                {placesT("color_selection")}
              </span>
              <div className="w-full grid grid-cols-8 grid-rows-2 gap-y-5 gap-x-4">
                {colors?.map((color, index) => (
                  <div
                    key={index}
                    style={{ backgroundColor: color }}
                    className={`w-6 h-6 rounded-full transition cursor-pointer ${
                      currentColor === index && "scale-125 ring-4"
                    }`}
                    onClick={() => {
                      setCurrentColor(index);
                      setCreateForm({
                        ...createForm,
                        color: color,
                      });
                    }}
                  />
                ))}
              </div>
            </div>
            <div className="w-full flex flex-col gap-2">
              <span className="font-semibold text-lg">
                {placesT("public_scope")}
              </span>
              <label
                htmlFor="public"
                className="w-full flex gap-4 items-center cursor-pointer"
              >
                <input
                  type="radio"
                  name="public"
                  id="public"
                  checked={createForm.isPublic}
                  onChange={(e) => {
                    setCreateForm({
                      ...createForm,
                      isPublic: e.target.checked,
                    });
                  }}
                  className="w-5 h-5"
                />
                <div className="w-full flex flex-col gap-0">
                  <span className="font-medium">{placesT("public")}</span>
                  <span className="text-sm text-gray-500">
                    {placesT("public_subtext")}
                  </span>
                </div>
              </label>
              <label
                htmlFor="private"
                className="w-full flex gap-4 items-center cursor-pointer"
              >
                <input
                  type="radio"
                  name="private"
                  id="private"
                  checked={!createForm.isPublic}
                  onChange={(e) => {
                    setCreateForm({
                      ...createForm,
                      isPublic: !e.target.checked,
                    });
                  }}
                  className="w-5 h-5"
                />
                <div className="w-full flex flex-col gap-0">
                  <span className="font-medium">{placesT("private")}</span>
                  <span className="text-sm text-gray-500">
                    {placesT("private_subtext")}
                  </span>
                </div>
              </label>
            </div>
            <div
              className={`w-full flex flex-col gap-0 transition ${
                !createForm.isPublic && "opacity-30"
              }`}
            >
              <span className="font-semibold text-base">
                {placesT("list_exposed")}
              </span>
              <div className="w-full flex items-center gap-1">
                <span className="w-full text-start text-xs text-black/50 dark:text-white/60 font-medium">
                  {placesT("list_exposed_subtext")}
                </span>
                <label
                  htmlFor="exposed"
                  className="relative cursor-pointer scale-75"
                >
                  <input
                    id="exposed"
                    name="exposed"
                    type="checkbox"
                    className="sr-only peer"
                    disabled={!createForm.isPublic}
                    checked={createForm.isExposed}
                    onChange={(e) =>
                      setCreateForm({
                        ...createForm,
                        isExposed: e.target.checked,
                      })
                    }
                  />
                  <div className="w-[53px] h-7 flex items-center bg-gray-300 rounded-full text-[11px] peer-checked:text-[#e5136f] text-gray-300 font-extrabold after:flex after:items-center after:justify-center peer after:content-['NO'] peer-checked:after:content-['YES'] peer-checked:after:translate-x-full after:absolute after:left-[2px] peer-checked:after:border-white after:bg-white after:border after:border-gray-300 after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-[#e5136f]" />
                </label>
              </div>
            </div>
            <div className="w-full flex flex-col gap-2">
              <span className="font-semibold text-lg">
                {placesT("write_detailed_description")}
              </span>
              <div className="w-full flex items-center justify-between gap-2 py-2 px-2 bg-gray-100 dark:bg-brown rounded">
                <input
                  type="text"
                  placeholder={placesT("leave_a_note_pl")}
                  className="w-full border-none outline-none bg-transparent text-sm caret-red"
                  maxLength={30}
                  value={createForm.note}
                  onChange={(e) => {
                    setCreateForm({
                      ...createForm,
                      note: e.target.value,
                    });
                  }}
                />
                <span className="text-xs text-nowrap">
                  {createForm?.note?.length}
                  <span className="text-black/40 dark:text-white/70">/30</span>
                </span>
              </div>
              <div className="w-full flex items-center justify-between gap-2 py-2 px-2 bg-gray-100 dark:bg-brown rounded">
                <input
                  type="url"
                  placeholder={placesT("relevant_url_pl")}
                  className="w-full border-none outline-none bg-transparent text-sm caret-red"
                  value={createForm.url}
                  onChange={(e) => {
                    setCreateForm({
                      ...createForm,
                      url: e.target.value,
                    });
                  }}
                />
              </div>
            </div>
          </div>
          <div className="w-full flex items-center justify-center py-4 px-4 border-t dark:border-brown">
            <button
              type="button"
              disabled={createForm?.listName?.length === 0 || loader}
              onClick={handleSubmit}
              className={`w-full p-2 rounded-md ${
                createForm?.listName?.length > 0 && !loader
                  ? "bg-red hover:bg-red/80"
                  : "bg-red/50 pointer-events-none cursor-not-allowed"
              } text-white text-lg text-nowrap transition flex items-center justify-center gap-1`}
            >
              {loader && (
                <Iconify
                  icon="line-md:loading-twotone-loop"
                  className={"!text-white animate-spin"}
                />
              )}
              {placesT("save")}
            </button>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default FavPlaceListCreateFormSliderModal;
